import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { MoreHorizontal, Shield, UserMinus, UserPlus, Trash2, Users, FileText, ShieldCheck, Pencil, Plus, Check, Clock, X, MailOpen, Signature, Lock } from "lucide-react";
import { NavLink } from "react-router-dom";
import UserFormDialog from "@/components/UserFormDialog";
import PermissionsDialog from "@/components/PermissionsDialog";
import ApproveAccessRequestDialog from "@/components/ApproveAccessRequestDialog";

function AdminSidebar() {
  const linkCls = ({ isActive }) =>
    `px-3 py-2 rounded-sm text-sm font-medium flex items-center gap-2 transition-colors ${
      isActive
        ? "bg-[#0B132B] text-white"
        : "text-[#0B132B] hover:bg-[#0B132B]/5"
    }`;
  return (
    <aside className="lg:sticky lg:top-20 lg:h-fit">
      <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37] mb-3">
        Admin
      </div>
      <nav className="flex lg:flex-col gap-1">
        <NavLink to="/admin/users" className={linkCls} data-testid="admin-nav-users">
          <Users size={15} /> Users
        </NavLink>
        <NavLink to="/admin/agencies" className={linkCls} data-testid="admin-nav-agencies">
          <ShieldCheck size={15} /> Agencies
        </NavLink>
        <NavLink to="/admin/agents" className={linkCls} data-testid="admin-nav-agents">
          <Signature size={15} /> Agents
        </NavLink>
        <NavLink to="/admin/proposals" className={linkCls} data-testid="admin-nav-proposals">
          <FileText size={15} /> All proposals
        </NavLink>
        <NavLink to="/admin/rates" className={linkCls} data-testid="admin-nav-rates">
          <FileText size={15} /> Programs & rates
        </NavLink>
        <NavLink to="/admin/production-rate-cards" className={linkCls} data-testid="admin-nav-production-rate-cards">
          <Lock size={15} /> Production rate cards
        </NavLink>
      </nav>
    </aside>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [accessRequests, setAccessRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [editingUser, setEditingUser] = useState(null);
  const [permsUser, setPermsUser] = useState(null);
  const [reqBusyId, setReqBusyId] = useState(null);
  const [approveRequest, setApproveRequest] = useState(null);

  const openCreate = () => {
    setDialogMode("create");
    setEditingUser(null);
    setDialogOpen(true);
  };
  const openEdit = (u) => {
    setDialogMode("edit");
    setEditingUser(u);
    setDialogOpen(true);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [u, s, r] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/stats"),
        api.get("/admin/access-requests").catch(() => ({ data: [] })),
      ]);
      setUsers(u.data);
      setStats(s.data);
      setAccessRequests(r.data || []);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const denyRequest = async (req) => {
    const reason = window.prompt(`Deny ${req.email}? Optional reason:`);
    if (reason === null) return; // user cancelled
    setReqBusyId(req.id);
    try {
      await api.post(`/admin/access-requests/${req.id}/deny`, { reason });
      toast.success("Request denied");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deny failed");
    } finally {
      setReqBusyId(null);
    }
  };

  const setRole = async (id, role) => {
    try {
      await api.put(`/admin/users/${id}/role`, { role });
      toast.success(`Role updated to ${role}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Delete ${email}? All their proposals will be removed.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const approve = async (id, email) => {
    try {
      await api.post(`/admin/users/${id}/approve`);
      toast.success(`${email} approved`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    }
  };

  const sendResetLink = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/send-reset-link`);
      toast.success(`Password link emailed to ${u.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send link");
    }
  };

  const pendingUsers = users.filter((u) => u.approved === false);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Administration
          </div>
          <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
            <div>
              <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] tracking-tight leading-none">
                User management
              </h1>
              <p className="text-sm text-[#6C757D] mt-3 max-w-xl leading-relaxed">
                Every account in the network. Add new agents, edit their details, or remove them entirely.
              </p>
            </div>
            <Button
              onClick={openCreate}
              className="bg-[#0B132B] hover:bg-[#0B132B]/90 h-10"
              data-testid="admin-add-user-btn"
            >
              <Plus size={15} className="mr-2" /> Add user
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
          <AdminSidebar />
          <div>
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E9ECEF] rounded-sm overflow-hidden border border-[#E9ECEF] mb-8" data-testid="admin-stats-strip">
                <Stat label="Users" value={stats.users} icon={<Users size={16} />} />
                <Stat label="Admins" value={stats.admins} icon={<ShieldCheck size={16} />} />
                <Stat label="Agents" value={stats.agents} icon={<Users size={16} />} />
                <Stat label="Proposals" value={stats.proposals} icon={<FileText size={16} />} />
              </div>
            )}

            {loading ? (
              <div className="text-sm text-[#6C757D]">Loading…</div>
            ) : (
              <>
                {accessRequests.length > 0 && (
                  <Card
                    className="mb-6 bg-white border-2 border-[#D4AF37]/40 rounded-sm overflow-hidden"
                    data-testid="admin-access-requests-card"
                  >
                    <div className="px-4 py-3 bg-[#D4AF37]/10 border-b border-[#D4AF37]/30 flex items-center gap-2">
                      <MailOpen size={15} className="text-[#D4AF37]" />
                      <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">
                        {accessRequests.length} Google access request{accessRequests.length === 1 ? "" : "s"} — awaiting review
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[#E9ECEF] text-xs uppercase tracking-[0.14em] text-[#6C757D]">
                            <th className="text-left px-4 py-2 font-semibold">Name</th>
                            <th className="text-left px-4 py-2 font-semibold">Email</th>
                            <th className="text-left px-4 py-2 font-semibold">Agency</th>
                            <th className="text-left px-4 py-2 font-semibold">Selling agent</th>
                            <th className="text-left px-4 py-2 font-semibold">Notes</th>
                            <th className="px-3 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {accessRequests.map((r) => (
                            <tr
                              key={r.id}
                              className="border-b border-[#E9ECEF] last:border-b-0 hover:bg-[#F8F9FA]"
                              data-testid={`admin-access-request-row-${r.id}`}
                            >
                              <td className="px-4 py-3 font-semibold text-[#0B132B]">{r.name}</td>
                              <td className="px-4 py-3 text-[#0B132B]/80">{r.email}</td>
                              <td className="px-4 py-3 text-[#6C757D]">{r.agency || "—"}</td>
                              <td className="px-4 py-3">
                                {r.selling_agent ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                                    <Check size={12} /> Yes
                                  </span>
                                ) : (
                                  <span className="text-xs text-[#6C757D]">No</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-xs text-[#6C757D] max-w-xs truncate">
                                {r.notes || "—"}
                              </td>
                              <td className="px-3 py-3">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => setApproveRequest(r)}
                                    disabled={reqBusyId === r.id}
                                    className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs"
                                    data-testid={`admin-access-request-approve-${r.id}`}
                                  >
                                    <Check size={13} className="mr-1" /> Approve…
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => denyRequest(r)}
                                    disabled={reqBusyId === r.id}
                                    className="h-8 rounded-sm text-xs border-red-200 text-red-700 hover:bg-red-50"
                                    data-testid={`admin-access-request-deny-${r.id}`}
                                  >
                                    <X size={13} className="mr-1" /> Deny
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
                {pendingUsers.length > 0 && (
                  <div
                    className="mb-6 rounded-sm border-2 border-[#D4AF37]/40 bg-[#D4AF37]/5 p-4 flex items-start gap-3"
                    data-testid="admin-pending-banner"
                  >
                    <div className="mt-0.5 w-8 h-8 rounded-full bg-[#D4AF37]/20 grid place-items-center shrink-0">
                      <Clock size={16} className="text-[#D4AF37]" />
                    </div>
                    <div className="flex-1">
                      <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">
                        {pendingUsers.length} agent{pendingUsers.length === 1 ? "" : "s"} awaiting approval
                      </div>
                      <div className="text-sm text-[#0B132B]/80 mt-1">
                        These accounts registered via the sign-up form and can't sign in until you approve them below.
                      </div>
                    </div>
                  </div>
                )}
                <Card className="bg-white border border-[#E9ECEF] rounded-sm overflow-hidden" data-testid="admin-users-table-card">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#0B132B] text-white text-xs uppercase tracking-[0.14em]">
                        <th className="text-left px-4 py-3 font-semibold">Name</th>
                        <th className="text-left px-4 py-3 font-semibold">Email</th>
                        <th className="text-left px-4 py-3 font-semibold">Agency</th>
                        <th className="text-left px-4 py-3 font-semibold">Role</th>
                        <th className="text-left px-4 py-3 font-semibold">Status</th>
                        <th className="text-right px-4 py-3 font-semibold">Proposals</th>
                        <th className="px-3 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => {
                        const pending = u.approved === false;
                        return (
                        <tr key={u.id} className={`border-b border-[#E9ECEF] hover:bg-[#F8F9FA] ${pending ? "bg-[#D4AF37]/[0.04]" : ""}`} data-testid={`admin-user-row-${u.id}`}>
                          <td className="px-4 py-3 font-semibold text-[#0B132B]">{u.name}</td>
                          <td className="px-4 py-3 text-[#6C757D]">{u.email}</td>
                          <td className="px-4 py-3 text-[#6C757D]">{u.agency || "—"}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] uppercase tracking-[0.14em] font-bold ${
                                u.role === "admin"
                                  ? "bg-[#D4AF37]/20 text-[#0B132B]"
                                  : "bg-[#0B132B]/5 text-[#0B132B]"
                              }`}
                            >
                              {u.role === "admin" && <Shield size={11} />}
                              {u.role}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {pending ? (
                              <span
                                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] uppercase tracking-[0.14em] font-bold bg-[#D4AF37]/20 text-[#0B132B]"
                                data-testid={`admin-status-pending-${u.id}`}
                              >
                                <Clock size={11} /> Pending
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] uppercase tracking-[0.14em] font-bold bg-emerald-100 text-emerald-800"
                                data-testid={`admin-status-active-${u.id}`}
                              >
                                <Check size={11} /> Active
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-[#0B132B]">{u.proposal_count}</td>
                          <td className="px-3 py-3">
                            <div className="flex items-center justify-end gap-2">
                              {pending && (
                                <Button
                                  size="sm"
                                  onClick={() => approve(u.id, u.email)}
                                  className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs"
                                  data-testid={`admin-approve-${u.id}`}
                                >
                                  <Check size={13} className="mr-1" /> Approve
                                </Button>
                              )}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`admin-user-menu-${u.id}`}>
                                    <MoreHorizontal size={16} />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {pending && (
                                    <DropdownMenuItem onClick={() => approve(u.id, u.email)} data-testid={`admin-approve-menu-${u.id}`}>
                                      <Check size={13} className="mr-2" /> Approve account
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`admin-edit-${u.id}`}>
                                    <Pencil size={13} className="mr-2" /> Edit user
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => setPermsUser(u)} data-testid={`admin-permissions-${u.id}`}>
                                    <ShieldCheck size={13} className="mr-2" /> Permissions
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => sendResetLink(u)} data-testid={`admin-send-reset-${u.id}`}>
                                    <MailOpen size={13} className="mr-2" /> Email password link
                                  </DropdownMenuItem>
                                  {u.role !== "admin" ? (
                                    <DropdownMenuItem onClick={() => setRole(u.id, "admin")} data-testid={`admin-promote-${u.id}`}>
                                      <UserPlus size={13} className="mr-2" /> Promote to admin
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => setRole(u.id, "agent")} data-testid={`admin-demote-${u.id}`}>
                                      <UserMinus size={13} className="mr-2" /> Demote to agent
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => remove(u.id, u.email)} className="text-red-600" data-testid={`admin-delete-${u.id}`}>
                                    <Trash2 size={13} className="mr-2" /> Delete user
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </Card>
              </>
            )}
          </div>
        </div>
      </main>
      <UserFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        user={editingUser}
        onSaved={load}
      />
      {permsUser && (
        <PermissionsDialog
          user={permsUser}
          onClose={() => setPermsUser(null)}
          onSaved={load}
        />
      )}
      <ApproveAccessRequestDialog
        open={!!approveRequest}
        request={approveRequest}
        onOpenChange={(o) => { if (!o) setApproveRequest(null); }}
        onApproved={() => { setApproveRequest(null); load(); }}
      />
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[#6C757D]">{label}</div>
        <div className="text-[#0B132B]/60">{icon}</div>
      </div>
      <div className="font-display text-3xl font-black text-[#0B132B] mt-2">{value}</div>
    </div>
  );
}

export { AdminSidebar };
